import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Button,
} from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";
import { supabase } from "../../logic/client";


export default function StayOrLeave(props: {handlePageChange: (page: string) => void}) {
  const { handlePageChange } = props;
  const { lang } = useI18n();
  const isDE = lang === "de";

  const [confirmLeaveOpen, setConfirmLeaveOpen] = React.useState(false);

  async function saveDecision(choice: "stay" | "leave") {
    const { data: userRes } = await supabase.auth.getUser();

    const { data: emp } = await supabase
      .from("employees")
      .select("id")
      .eq("auth_user_id", userRes?.user?.id)
      .single();

    const { data: workshop } = await supabase
      .from("workshops")
      .select("id")
      .eq("is_active", true)
      .single();

    if (!emp || !workshop) {
      console.error("Missing employee/workshop");
      return;
    }

    const { error } = await supabase
      .from("workshop_participant_decisions")
      .upsert({
        workshop_id: workshop.id,
        employee_id: emp.id,
        stay_or_leave: choice,
        resilience_type: null, // reset if changing
      });

    if (error) {
      console.error("SAVE ERROR:", error);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 48px)",
        width: "100%",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
        backgroundImage: `
          linear-gradient(
            rgba(8, 8, 8, 0.78),
            rgba(8, 8, 8, 0.82)
          ),
          url("/scenarioBgs/leaveOrstay.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Paper
        sx={{
          width: { xs: "100%", sm: "min(88vw, 780px)", md: "min(78vw, 860px)" },
          borderRadius: 5,
          p: { xs: 4, sm: 6, md: 7 },
          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49, 32, 32, 0.95), rgba(34, 21, 21, 0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
          textAlign: "center",
        }}
      >
        <Stack spacing={5} alignItems="center">
          
          {/* TITLE */}
          <Typography
            sx={{
              color: "#fff",
              fontSize: { xs: "1.6rem", sm: "2rem", md: "2.3rem" },
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              lineHeight: 1.3,
            }}
          >
            {isDE ? "Gehen oder Bleiben?" : "Stay or Leave?"}
          </Typography>

          {/* QUESTION */}
          <Typography
            sx={{
              color: "rgba(255,255,255,0.85)",
              fontSize: { xs: "1rem", sm: "1.15rem" },
              fontWeight: 500,
              maxWidth: 600,
            }}
          >
            {isDE
              ? "Bleibst du in Deutschland oder verlässt du das Land kurz- oder langfristig?"
              : "Will you stay in Germany or leave the country in the short or long term?"}
          </Typography>

          {/* BUTTONS */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            sx={{ width: "100%", justifyContent: "center" }}
          >
            {/* LEAVE */}
            <Button
              variant="contained"
              onClick={() => setConfirmLeaveOpen(true)}
              sx={{
                minWidth: 160,
                py: 1.5,
                fontWeight: 700,
                background: "linear-gradient(90deg, #b50d20, #d6293e)",
                "&:hover": {
                  background: "linear-gradient(90deg, #a10c1c, #bf2436)",
                },
              }}
            >
              {isDE ? "Gehen" : "Leave"}
            </Button>

            {/* STAY */}
            <Button
              variant="outlined"
              onClick={async () => {
                await saveDecision("stay");
                handlePageChange("StayDecision");
              }}
              sx={{
                minWidth: 160,
                py: 1.5,
                fontWeight: 700,
                color: "#fff",
                borderColor: "rgba(255,255,255,0.5)",
                "&:hover": {
                  borderColor: "#fff",
                  background: "rgba(255,255,255,0.05)",
                },
              }}
            >
              {isDE ? "Bleiben" : "Stay"}
            </Button>
          </Stack>

        </Stack>
      </Paper>
      {confirmLeaveOpen && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.75)",
            backdropFilter: "blur(6px)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
          }}
        >
          <Paper
            sx={{
              width: "min(90vw, 480px)",
              borderRadius: 4,
              p: 4,
              textAlign: "center",
              background:
                "linear-gradient(180deg, rgba(49, 32, 32, 0.98), rgba(34, 21, 21, 0.98))",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <Stack spacing={3} alignItems="center">

              {/* TITLE */}
              <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1.3rem" }}>
                {isDE
                  ? "Bist du sicher, dass du gehen willst?"
                  : "Are you sure you want to leave?"}
              </Typography>

              {/* SUBTEXT */}
              <Typography sx={{ color: "rgba(255,255,255,0.7)" }}>
                {isDE
                  ? "Diese Entscheidung verändert deinen weiteren Verlauf."
                  : "This decision will change your path forward."}
              </Typography>

              {/* BUTTONS */}
              <Stack direction="row" spacing={2}>

                {/* CANCEL */}
                <Button
                  variant="outlined"
                  onClick={() => setConfirmLeaveOpen(false)}
                  sx={{
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.4)",
                  }}
                >
                  {isDE ? "Abbrechen" : "Cancel"}
                </Button>

                {/* CONFIRM */}
                <Button
                  variant="contained"
                  onClick={async () => {
                    await saveDecision("leave");
                    setConfirmLeaveOpen(false);
                    handlePageChange("StayDecision");
                  }}
                  sx={{
                    background: "linear-gradient(90deg, #b50d20, #d6293e)",
                    fontWeight: 700,
                  }}
                >
                  {isDE ? "Ja, gehen" : "Yes, leave"}
                </Button>

              </Stack>

            </Stack>
          </Paper>
        </Box>
      )}
    </Box>
  );
}